// The reviver's third argument, `context.source`, is the raw JSON text
// for the current value. Older Firefox/Safari don't ship it, so the
// polyfill is required to recover full-precision digits below.
import "core-js/actual/json/parse";

// JSON keys whose values are nanosecond-epoch integers. The reviver
// allowlists by key name, so adding an entry here coerces that field to
// `bigint` everywhere the reviver runs. `Number` would silently round
// ns timestamps past `Number.MAX_SAFE_INTEGER` (≈ April 15, 1970).
//
// Topic and Event records: `start_time`, `end_time`.
// Session aggregate bounds: `min_timestamp_ns`, `max_timestamp_ns`.
// SessionFile contribution window: `range_min_timestamp_ns`,
// `range_max_timestamp_ns`.
// Magic-link context object: `t`.
const COERCED_KEYS = new Set<string>([
  "start_time",
  "end_time",
  "t",
  "min_timestamp_ns",
  "max_timestamp_ns",
  "range_min_timestamp_ns",
  "range_max_timestamp_ns",
]);

/**
 * `JSON.parse` reviver that coerces every key in `COERCED_KEYS` to
 * `bigint` by re-parsing the raw token text from `context.source`.
 * Without this, ns-epoch values lose precision in the default parse.
 *
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/JSON/parse#the_reviver_parameter
 * https://github.com/tc39/proposal-json-parse-with-source
 */
export function parseTimestampsAsBigInt(
  key: string,
  value: unknown,
  context?: { source: string },
) {
  // `context` is absent on engines that lack the source-text arg when
  // the polyfill fails to install; `null` is valid for nullable bounds.
  // Both fall through to `value`.
  if (COERCED_KEYS.has(key) && context !== undefined && value !== null) {
    try {
      return BigInt(context.source);
    } catch {
      // `BigInt(...)` throws when the token isn't an integer — most
      // often a customer's user-metadata reusing one of these keys with
      // a non-integer value. Fall back to the lossy Number so the field
      // round-trips; first-class Roboto fields are always integers and
      // never reach this branch.
      return value;
    }
  }

  return value;
}
